import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { API, http } from "@/lib/api";

// Build a public URL for an uploaded asset id (served by GET /api/files/{id}, no auth).
export const assetUrl = (id) => {
  if (!id) return "";
  if (typeof id === "string" && /^https?:\/\//i.test(id)) return id;
  return `${API}/files/${id}`;
};

// Pick readable text color (black/white) for a given brand background hex.
export const brandTextColor = (hex) => {
  try {
    const h = (hex || "#6366F1").replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? "#050505" : "#FFFFFF";
  } catch {
    return "#FFFFFF";
  }
};

export const fetchBranding = async () => (await http.get("/public/branding")).data;

export function useBranding() {
  const { data } = useQuery({
    queryKey: ["public-branding"],
    queryFn: fetchBranding,
    staleTime: 0,
  });
  return data || null;
}

// Applies the active brand identity globally: browser title, favicon and the --brand-color
// CSS variable. Mount once near the app root so every page reflects the saved branding.
export function BrandApplier() {
  const b = useBranding();
  // Buyer network branding wins over the global platform brand — survives login, refresh
  // and direct internal URLs. Anonymous/staff get the platform brand as before.
  const { data: mine } = useQuery({
    queryKey: ["my-branding"],
    queryFn: async () => (await http.get("/public/my-branding")).data,
    refetchInterval: 60000, retry: false,
  });
  useEffect(() => {
    const setFavicon = (url) => {
      if (!url) return;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = url;
    };
    if (mine?.found) {
      document.title = mine.network_name || "Affiliate Network";
      // Tenant assets are stored as /api/... paths (portal uploads) — resolve like the portal
      // login page does, NOT via the platform assetUrl (/api/files/<id>) which 404s on paths.
      const resolve = (v) => (!v ? "" : (/^https?:\/\//i.test(v) ? v : (v.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${v}` : assetUrl(v))));
      setFavicon(resolve(mine.branding?.favicon_url) || resolve(mine.branding?.logo_url));
      return;
    }
    if (!b) return;
    const name = b.brand?.name || "Tracify";
    const title = b.seo?.title || `${name} — ${b.brand?.tagline || "Affiliate platform"}`;
    const desc = b.seo?.description || b.landing?.subheadline || "";
    document.title = title;
    const color = b.brand?.accent_color || "#6366F1";
    document.documentElement.style.setProperty("--brand-color", color);
    setFavicon(assetUrl(b.brand?.favicon_id));
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[${attr}='${key}']`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setMeta("name", "description", desc);
    setMeta("property", "og:site_name", name);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    const abs = (u) => { try { return u ? new URL(u, window.location.origin).href : ""; } catch { return u || ""; } };
    const og = abs(assetUrl(b.seo?.og_image_id));
    if (og) {
      setMeta("property", "og:image", og);
      setMeta("name", "twitter:image", og);
      setMeta("name", "twitter:card", "summary_large_image");
    }
  }, [b, mine]);
  return null;
}
